import { useEffect, useMemo, useRef, useState } from 'react';

type UseScrollProps = {
  onRefresh?: () => void | Promise<void>;
  onHardRefresh?: () => void | Promise<void>;
  enabled?: boolean;
  softRefreshThreshold?: number;
  maxPullDistance?: number;
  resistance?: number;
  holdDuration?: number;
};

export type PullRefreshState =
  | 'idle'
  | 'pull'
  | 'ready'
  | 'ready-hard'
  | 'refreshing';

const applyResistance = (
  rawDistance: number,
  resistance: number,
  maxPullDistance: number
) => Math.min(maxPullDistance, Math.max(0, rawDistance * resistance));

// Hard refresh has been disabled for now. Keep the implementation here so it
// can be restored later if the hard-refresh gesture is enabled again.
// const runDefaultHardRefresh = async () => {
//   if (!('serviceWorker' in navigator)) {
//     window.location.reload();
//     return;
//   }
//
//   const registration = await navigator.serviceWorker.getRegistration();
//   if (!registration) {
//     window.location.reload();
//     return;
//   }
//
//   const activateWaitingWorker = () => {
//     registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
//   };
//
//   activateWaitingWorker();
//   await registration.update();
//   activateWaitingWorker();
//
//   await new Promise<void>((resolve) => {
//     let settled = false;
//
//     const finish = () => {
//       if (settled) return;
//       settled = true;
//       resolve();
//     };
//
//     const timeoutId = window.setTimeout(finish, 1200);
//
//     navigator.serviceWorker.addEventListener(
//       'controllerchange',
//       () => {
//         window.clearTimeout(timeoutId);
//         finish();
//       },
//       { once: true }
//     );
//
//     if (registration.installing) {
//       registration.installing.addEventListener('statechange', () => {
//         activateWaitingWorker();
//       });
//     } else if (!registration.waiting) {
//       window.clearTimeout(timeoutId);
//       finish();
//     }
//   });
//
//   window.location.reload();
// };
//
// const HOLD_TOLERANCE = 6;

export const useScroll = ({
  onRefresh,
  // onHardRefresh,
  enabled = true,
  softRefreshThreshold = 80,
  maxPullDistance = 168,
  resistance = 0.8,
  // holdDuration = 500,
}: UseScrollProps = {}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [pullDistance, setPullDistance] = useState(0);
  const [refreshState, setRefreshState] = useState<{
    refreshing: boolean;
    phase: PullRefreshState;
  }>({ refreshing: false, phase: 'idle' });

  const startY = useRef<number | null>(null);
  const startX = useRef<number | null>(null);
  // const lastY = useRef<number | null>(null);

  const isPulling = useRef(false);
  // const holdTimer = useRef<number | null>(null);
  // const hardReady = useRef(false);
  const isRefreshingRef = useRef(false);

  const finalDistance = useRef(0);

  // const clearHold = () => {
  //   hardReady.current = false;
  //   if (holdTimer.current !== null) {
  //     clearTimeout(holdTimer.current);
  //     holdTimer.current = null;
  //   }
  // };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enabled || isRefreshingRef.current || e.touches.length !== 1) return;

    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop > 0) return;

    startY.current = e.touches[0].clientY;
    startX.current = e.touches[0].clientX;
    // lastY.current = e.touches[0].clientY;

    isPulling.current = false;
    finalDistance.current = 0;

    // clearHold();
  };

  const handleTouchEnd = async () => {
    // const heldLongEnough = hardReady.current;

    const shouldSoftRefresh = finalDistance.current >= softRefreshThreshold;

    // const shouldHardRefresh = heldLongEnough;

    if (shouldSoftRefresh && !isRefreshingRef.current) {
      setRefreshState({ refreshing: true, phase: 'refreshing' });

      try {
        // snap back immediately
        setPullDistance(0);

        // Hard refresh disabled.
        // if (shouldHardRefresh) {
        //   if (onHardRefresh) await onHardRefresh();
        //   else await runDefaultHardRefresh();
        // } else {
        if (onRefresh) await onRefresh();
        else window.location.reload();
        // }
      } finally {
        setRefreshState({ refreshing: false, phase: 'idle' });
      }
    } else {
      setPullDistance(0);
      setRefreshState({ refreshing: false, phase: 'idle' });
    }

    startY.current = null;
    startX.current = null;
    // lastY.current = null;
    isPulling.current = false;
    finalDistance.current = 0;

    // clearHold();
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleMove = (e: TouchEvent) => {
      if (
        !enabled ||
        startY.current === null ||
        startX.current === null ||
        e.touches.length !== 1
      )
        return;

      if (isRefreshingRef.current) return;

      const scrollTop = el.scrollTop;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY.current;
      const deltaX = e.touches[0].clientX - startX.current;

      if (Math.abs(deltaX) > Math.abs(deltaY) || deltaY <= 0 || scrollTop > 0) {
        setPullDistance(0);
        setRefreshState({ refreshing: false, phase: 'idle' });
        isPulling.current = false;
        // lastY.current = null;
        // clearHold();
        return;
      }

      const distance = applyResistance(deltaY, resistance, maxPullDistance);
      isPulling.current = distance > 0;

      if (!isPulling.current) return;

      e.preventDefault();

      setPullDistance(distance);
      finalDistance.current = distance;

      // Hard refresh hold logic disabled.
      // const movement =
      //   lastY.current !== null ? Math.abs(currentY - lastY.current) : 0;
      // lastY.current = currentY;
      //
      // if (distance >= softRefreshThreshold) {
      //   if (movement > HOLD_TOLERANCE) {
      //     clearHold();
      //   }
      //
      //   if (
      //     movement <= HOLD_TOLERANCE &&
      //     holdTimer.current === null &&
      //     !hardReady.current
      //   ) {
      //     holdTimer.current = window.setTimeout(() => {
      //       if (isRefreshingRef.current) return;
      //
      //       hardReady.current = true;
      //       setRefreshState({ refreshing: true, phase: 'ready-hard' });
      //       if (navigator.vibrate) {
      //         navigator.vibrate(10);
      //       }
      //     }, holdDuration);
      //   }
      // } else {
      //   clearHold();
      // }

      // STATE
      // if (!hardReady.current) {
      setRefreshState({
        refreshing: true,
        phase: distance >= softRefreshThreshold ? 'ready' : 'pull',
      });
      // }
    };

    el.addEventListener('touchmove', handleMove, { passive: false });

    return () => {
      el.removeEventListener('touchmove', handleMove);
    };
  }, [
    enabled,
    refreshState,
    resistance,
    maxPullDistance,
    softRefreshThreshold,
    // holdDuration,
  ]);

  const refreshLabel = useMemo(() => {
    if (refreshState.phase === 'refreshing') return 'Refreshing...';
    // if (refreshState.phase === 'ready-hard') return 'Release for hard refresh';
    if (refreshState.phase === 'ready') return 'Release to refresh';
    return 'Pull to refresh';
  }, [refreshState.phase]);

  const contentStyle = useMemo(
    () => ({
      transform: `translateY(${pullDistance}px)`,
      transition:
        pullDistance > 0 || refreshState.refreshing
          ? 'none'
          : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
      willChange: pullDistance ? 'transform' : 'auto',
    }),
    [pullDistance]
  );

  return {
    scrollRef,
    pullDistance,
    isRefreshing: refreshState.refreshing,
    refreshState: refreshState.phase,
    refreshLabel,
    contentStyle,
    showPullIndicator:
      refreshState.refreshing ||
      pullDistance > 0 ||
      refreshState.phase !== 'idle',
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove: () => {},
  };
};
